import React from 'react';
import { Button } from 'antd';
import RecordBadge, { RecordIconTile } from '../common/RecordBadge';
import { normalizeId } from '../../utils/ids';
import { borrowedFromOf, sourceQualifierOf } from '../../utils/linkedRecords';
import { owningCollectionIdOf } from '../../utils/recordQualifier';
import { useOptionalFavorites } from '../../contexts/FavoritesContext';
import { COLLECTION_ICON, PERSON, PIN, PIN_ANGLE } from '../icons';

// Compatibility alias: id normalization lives in utils/ids now (it applies
// to items and collections too, not just cards). Prefer importing
// normalizeId from utils/ids in new code.
export { normalizeId as normalizeCardId };

/**
 * Thin alias for the shared badge tile, kept so existing call sites keep
 * working. Prefer RecordBadge / RecordIconTile from
 * components/common/RecordBadge in new code — a tile on its own is only
 * right where the name is spoken elsewhere on the same row.
 */
export function CardIconSwatch({ card, size = 27 }) {
	return <RecordIconTile record={card} kind="card" size={size} />;
}

/**
 * A card definition inside a picker: the record badge at its smallest,
 * indented by hierarchy depth (the indent is outside the badge — nothing
 * shares the badge's own space). `collection` is the collection that owns
 * the card, and by default it is read off the card (`cardOwnerOf`): a card a
 * link lends, a parent's card in a sub-collection, a sub-collection's card at
 * its parent, a global collection's card — each then reads as the QUALIFIED
 * card badge wherever the picker is not that collection (RecordBadge
 * `collection`; nothing shows on the card's own collection, and a global
 * card belongs to none). Pass an identity to name it better, or
 * `collection={null}` where the picker already says it.
 */
export function CardOptionLabel({ card, depth = 0, collection, maxChars }) {
	const owner = collection === undefined ? cardOwnerOf(card) : collection;
	const name = card?.name || '(untitled card)';
	// A picker with a row to fit says how many characters a name may show
	// (KPR-1634: 32). A longer one ellipsizes, and the whole name stays in the
	// tooltip and, unqualified, the accessible name.
	const clipped = maxChars && name.length > maxChars;
	const shown = clipped ? `${name.slice(0, maxChars - 1).trimEnd()}…` : name;
	return (
		<span className="card-option" style={{ paddingLeft: depth * 12 }}>
			<RecordBadge
				kind="card"
				record={card}
				title={shown}
				size="xs"
				collection={owner}
				{...(clipped ? { tooltip: name, ...(owner ? null : { 'aria-label': name }) } : null)}
			/>
		</span>
	);
}

/** A card a collection link lends, as its qualified card badge (utils/lookupScope linkedLookupGroup `labelFor`). */
export const linkedCardLabel = (card, from) => (
	<CardOptionLabel card={card} collection={sourceQualifierOf(from)} />
);

/**
 * The collection that owns a card, for CardOptionLabel / RecordBadge
 * `collection`: the lender for a borrowed card (its identity when the API
 * named it, else its id), else the owning collection's id (null for a
 * global card).
 */
export function cardOwnerOf(card) {
	const lender = borrowedFromOf(card);
	if (lender) return sourceQualifierOf(lender);
	return owningCollectionIdOf(card, 'card');
}

/**
 * The plain-text needle a card picker searches on.
 *
 * Card picker options carry a ReactNode `label` (icon tile + indented name), and
 * antd's default filterOption stringifies the label — a React element
 * stringifies to nothing useful, so every card search matched nothing ("No
 * data"). Every builder here therefore emits a `searchText` beside the label,
 * and InputSelect's filterOption (components/Input.js) matches on it.
 *
 * Ancestor names are folded in so a nested card is findable by where it lives:
 * a "CPR" card under "Staff" answers to "staff cpr" as well as to "cpr".
 */
export function cardSearchText(card, ancestorNames = []) {
	return [...ancestorNames, card?.name || '']
		.filter(Boolean)
		.join(' ');
}

/**
 * Hierarchy-ordered walk of a card set. Each entry is
 * `{card, depth, ancestorNames}` — `ancestorNames` runs root-first and excludes
 * the card itself, so callers can build breadcrumbs and search text without
 * re-walking the tree.
 */
export function buildCardHierarchyList(cards = []) {
	if (!Array.isArray(cards) || cards.length === 0) return [];
	const byId = new Map();
	const childrenMap = new Map();
	cards.forEach(card => {
		const id = normalizeId(card?._id);
		if (id) byId.set(id, card);
		const parentId = normalizeId(card?.parentCardId) || null;
		if (!childrenMap.has(parentId)) childrenMap.set(parentId, []);
		childrenMap.get(parentId).push(card);
	});
	const sortByName = list => list.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
	childrenMap.forEach(sortByName);
	const visited = new Set();
	const ordered = [];
	const visit = (card, depth, ancestorNames = []) => {
		const id = normalizeId(card?._id);
		if (!id || visited.has(id)) return;
		visited.add(id);
		ordered.push({ card, depth, ancestorNames });
		const children = childrenMap.get(id) || [];
		const trail = [...ancestorNames, card?.name].filter(Boolean);
		children.forEach(child => visit(child, depth + 1, trail));
	};
	const roots = cards.filter(card => {
		const parentId = normalizeId(card?.parentCardId);
		return !parentId || !byId.has(parentId);
	});
	sortByName(roots);
	roots.forEach(card => visit(card, 0));
	cards.forEach(card => visit(card, 0)); // include any remaining
	return ordered;
}

/**
 * Hierarchy-ordered picker options for a collection's MEMBER cards: walks the
 * full definition hierarchy, keeps only members, and indents each card by how
 * many of its ANCESTORS are also members — so gaps in the membership set
 * don't over-indent. Returns [{ value, card, depth }]; callers attach their
 * own label (usually <CardOptionLabel card depth/>). One implementation for
 * every member-card picker (Add item, sharing scope, ...).
 */
export function buildMemberCardOptions(cards = [], memberIds = new Set()) {
	const byId = new Map();
	(cards || []).forEach(card => {
		const id = normalizeId(card?._id);
		if (id) byId.set(id, card);
	});
	const inSetDepth = (card) => {
		let depth = 0;
		const seen = new Set();
		let parent = byId.get(normalizeId(card?.parentCardId));
		while (parent) {
			const pid = normalizeId(parent?._id);
			if (!pid || seen.has(pid)) break;
			seen.add(pid);
			if (memberIds.has(pid)) depth += 1;
			parent = byId.get(normalizeId(parent?.parentCardId));
		}
		return depth;
	};
	return buildCardHierarchyList(cards)
		.map(({ card, ancestorNames }) => {
			const value = normalizeId(card?._id);
			if (!value || !memberIds.has(value)) return null;
			// Search text folds in EVERY ancestor, member or not: the user types
			// what they know the card is under, not what the membership set says.
			return { value, card, depth: inSetDepth(card), searchText: cardSearchText(card, ancestorNames) };
		})
		.filter(Boolean);
}

/**
 * Pins the favorite cards (the pin icon's cards; stored as favorites) above
 * the rest of the hierarchy as an antd option group, headed Pinned. Options
 * must carry {value, card, depth}; favorites render flat (depth 0) and get a distinct key so React doesn't warn about the value
 * appearing in both groups. Returns the flat list untouched when no favorite
 * matches.
 */
export function groupFavoriteCardOptions(options = [], isFavoriteId) {
	if (typeof isFavoriteId !== 'function') return options;
	// Collection favorites sit above user favorites: the collection's own
	// standard leads, then whatever the user pinned for themselves. Both stay in
	// the one "Favorites" group. Sort is stable, so each tier keeps name order.
	const rank = kind => (kind === FAVORITE_KIND.COLLECTION ? 0 : 1);
	const favorites = options
		.map(option => ({ option, kind: option?.value ? isFavoriteId(option.value) : null }))
		.filter(entry => !!entry.kind)
		.sort((a, b) => rank(a.kind) - rank(b.kind))
		.map(({ option }) => ({
			...option,
			key: `favorite:${option.value}`,
			depth: 0,
			label: <CardOptionLabel card={option.card} depth={0} maxChars={option.maxChars} />,
		}));
	if (!favorites.length) return options;
	return [
		{ label: 'Pinned', options: favorites },
		{ label: 'Other cards', options },
	];
}

/**
 * The two ways a card can be pinned in a picker.
 *
 * COLLECTION — the collection's manager pinned it in collection settings, so it
 *   is pinned for everyone who uses the collection. Read-only here.
 * USER — the signed-in user pinned it for themselves. Toggleable here.
 *
 * A card that is both reads as a collection favorite: the pin the user cannot
 * remove is the one worth showing.
 */
export const FAVORITE_KIND = { COLLECTION: 'collection', USER: 'user' };

/**
 * Resolves a card id to a FAVORITE_KIND (or null). `collectionFavoriteIds` is
 * the Set from getCollectionFavoriteCardIds(collection); `isUserFavorite` is
 * usually `id => favorites.isFavoriteCard(collectionId, id)`.
 */
export function resolveFavoriteKind(cardId, collectionFavoriteIds, isUserFavorite) {
	const id = normalizeId(cardId);
	if (!id) return null;
	if (collectionFavoriteIds?.has(id)) return FAVORITE_KIND.COLLECTION;
	if (typeof isUserFavorite === 'function' && isUserFavorite(id)) return FAVORITE_KIND.USER;
	return null;
}

/**
 * Dropdown row for card pickers: the card label plus its pin.
 *
 * A pin, not a star (KPR-1634: pins replace stars). The stored data and the
 * code keep the name "favorite".
 *
 * Collection favorites render the pin on, with a collection glyph and no click
 * target — a user cannot unpin what the manager pinned, and a pin that
 * silently did nothing would be worse than one that is plainly inert. User
 * favorites render the same pin with a person glyph and stay toggleable.
 *
 * optionRender={option => (
 *   <CardPickerOptionRow option={option} collectionId={id}
 *     collectionFavoriteIds={ids} />
 * )}
 *
 * The pin is omitted when there is no FavoritesProvider (standalone tests).
 *
 * `showScope={false}` drops the tier glyph (and its slot) but keeps the pin
 * and its toggle — for a surface that says whose favorite it is elsewhere,
 * like the Add item modal's launch row.
 */
export function CardPickerOptionRow({ option, collectionId, collectionFavoriteIds, showScope = true }) {
	const favorites = useOptionalFavorites();
	const data = option?.data || {};
	const label = <CardOptionLabel card={data.card} depth={data.depth || 0} maxChars={data.maxChars} />;
	if (!favorites || !collectionId || !data.value) return label;

	const kind = resolveFavoriteKind(
		data.value,
		collectionFavoriteIds,
		id => favorites.isFavoriteCard(collectionId, id)
	);

	// Pin and scope glyph each get a fixed slot, and the scope slot is rendered
	// even when empty — that is what keeps one straight column of pins down the
	// list whether a row is unfavorited, user-favorited, or collection-favorited.
	// Without scope the slot goes too, so the pins still line up, just tighter.
	const badge = (PinIcon, ScopeIcon, on = true) => (
		<span className={[
			'card-picker-option__badge',
			on ? 'card-picker-option__badge--on' : '',
			showScope ? '' : 'card-picker-option__badge--no-scope',
		].filter(Boolean).join(' ')}>
			<PinIcon className="card-picker-option__badge-pin" aria-hidden="true" />
			{showScope ? (
				<span className="card-picker-option__badge-scope">
					{ScopeIcon ? <ScopeIcon aria-hidden="true" /> : null}
				</span>
			) : null}
		</span>
	);

	if (kind === FAVORITE_KIND.COLLECTION) {
		return (
			<span className="card-picker-option">
				{label}
				<span
					className="card-picker-option__mark card-picker-option__mark--collection"
					title="Pinned for everyone by a collection manager — managed in collection settings"
					aria-label="Pinned for everyone"
				>
					{badge(PIN, COLLECTION_ICON)}
				</span>
			</span>
		);
	}

	const isUserFavorite = kind === FAVORITE_KIND.USER;
	return (
		<span className="card-picker-option">
			{label}
			<Button
				type="text"
				size="small"
				className="card-picker-option__mark card-picker-option__pin"
				aria-label={isUserFavorite ? 'Unpin card' : 'Pin card'}
				aria-pressed={isUserFavorite}
				title={isUserFavorite ? 'Unpin card' : 'Pin card'}
				icon={badge(
					isUserFavorite ? PIN : PIN_ANGLE,
					isUserFavorite ? PERSON : null,
					isUserFavorite
				)}
				onMouseDown={event => { event.preventDefault(); event.stopPropagation(); }}
				onClick={event => {
					event.preventDefault();
					event.stopPropagation();
					favorites.toggleCardFavorite(collectionId, data.value);
				}}
			/>
		</span>
	);
}

export function buildCardHierarchyOptions(cards = []) {
	return buildCardHierarchyList(cards)
		.map(({ card, depth, ancestorNames }) => {
			const value = normalizeId(card?._id);
			if (!value) return null;
			return {
				value,
				card,
				depth,
				label: <CardOptionLabel card={card} depth={depth} />,
				// The plain-text twin of the ReactNode label — see cardSearchText.
				searchText: cardSearchText(card, ancestorNames),
			};
		})
		.filter(Boolean);
}
